import FilterAndSort from '@organisms/FilterAndSort/FilterAndSort';
import ProductContainer from '@molecules/ProductContainer/ProductContainer';
import { BreadcrumbDataSearchStateData, FacetSearchPageData } from '@occ/api-client';
import useVouchers from '@hooks/useVouchers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGridTemplateColumns from '@hooks/useGridTemplateColumns';
import useMediaQueryKey from '@hooks/useMediaQueryKey';
import usePackageData from '@hooks/usePackageData';
import packageComponentHelper from '@helpers/packageComponentHelper';
import useCmsComponents from '@hooks/useCmsComponents';

/**
 * Render promotionOffers for given type
 * @param type - online | offline
 * @returns {*}
 */
type Props = {
  storeType: 'online' | 'offline';
  generalCampaigns?: FacetSearchPageData | null;
  isLoading: boolean;
  hideAddToList?: boolean;
  showProgress?: boolean;
  personalCampaigns?: FacetSearchPageData | null;
  isPreviewMode?: boolean;
  cmsComponents?: any[];
};

const PromotionOffersFragment = ({
  storeType,
  generalCampaigns,
  hideAddToList = false,
  personalCampaigns,
  isPreviewMode = false,
  cmsComponents,
}: Props) => {
  const { pagination, results: products, breadcrumbs, facets } = generalCampaigns || {};
  const [stableFacets, setStableFacets] = useState(facets);
  const [stableBreadcrumbs, setStableBreadcrumbs] = useState(breadcrumbs);
  const [stableTotalResults, setStableTotalResults] = useState(pagination?.totalNumberOfResults);

  useEffect(() => {
    if (facets) setStableFacets(facets);
    if (breadcrumbs !== undefined) setStableBreadcrumbs(breadcrumbs);
    if (pagination?.totalNumberOfResults !== undefined) setStableTotalResults(pagination.totalNumberOfResults);
  }, [facets, breadcrumbs, pagination?.totalNumberOfResults]);
  const { onlineVouchers, offlineVouchers } = useVouchers(isPreviewMode);
  const promotionVouchers = storeType === 'online' ? onlineVouchers : offlineVouchers;

  const [ref, setRef] = useState<HTMLDivElement | null>(null);
  const [itemsPerRow, setItemsPerRow] = useState(0);
  const packageHelper = packageComponentHelper();
  const getCurrentMediaQueryKey = useMediaQueryKey();

  const promotionsComp = useMemo(() => {
    if (!cmsComponents || !cmsComponents.length) return null;
    return cmsComponents.find((c: any) => c?.typeCode === 'AxfoodPromotionsCMSComponent');
  }, [cmsComponents]);

  const { data: gridPackages } = useCmsComponents(
    JSON.stringify(promotionsComp?.gridPackages?.replace(/[[\]"]+/g, ''))
  );

  const packageKeyObject = useMemo(() => {
    return gridPackages?.component?.reduce((acc, item) => {
      // @ts-ignore
      if (item.uid) acc[item.uid] = item;
      return acc;
    }, {});
  }, [gridPackages]);

  const gridPackageData = usePackageData(packageKeyObject);

  useGridTemplateColumns(setItemsPerRow, ref, false);

  const handleRef = useCallback((node: HTMLDivElement) => {
    setRef(node);
  }, []);

  const items = useMemo(() => {
    const allProducts = [...((personalCampaigns?.results as any) ?? []), ...(products ?? [])];
    if (itemsPerRow === 0 || !products) return allProducts;

    return packageHelper.positionPackagesInList(allProducts, gridPackageData, getCurrentMediaQueryKey(), itemsPerRow);
  }, [itemsPerRow, products, personalCampaigns, gridPackageData, packageHelper, getCurrentMediaQueryKey]);

  return (
    <div ref={handleRef}>
      {stableFacets && (
        <FilterAndSort
          filterOptions={stableFacets}
          selectedFilters={stableBreadcrumbs as Array<BreadcrumbDataSearchStateData>}
          totalResults={stableTotalResults}
        />
      )}

      {products && products?.length > 0 && (
        <ProductContainer
          title={''}
          products={items}
          productType={storeType}
          vouchers={promotionVouchers}
          category={`promotion_page | ${storeType === 'offline' ? 'store' : 'ecommerce'} | generic`}
          offline={storeType === 'offline'}
          hideAddToList={hideAddToList}
          numberOfGridCols={itemsPerRow}
        />
      )}
    </div>
  );
};

export default PromotionOffersFragment;
